/**
 * Plan a School Trip — full-page composer (violet Bus Tracking look, matches
 * School Trip.dc.html). Writes the schoolTrips doc client-side and calls the
 * Spark-safe Render endpoints to create the hired bus + temp conductor logins
 * and notify parents. Ends on a "Trip created" success screen with the
 * show-once conductor credentials to hand over.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { addDoc, collection, doc, updateDoc, serverTimestamp, Timestamp, getDocs, limit, orderBy, query, where, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { createHireBus, addTripConductor, notifyTripParents, createTripParent, isTripsApiConfigured, ConductorCreated, TripParentCreated } from "../services/tripsApi";
import { addLinkedChildToTrip, listSchoolLinks, type FamilySchoolLink } from "../services/paymentsApi";
import { fetchPastTripCrew, type PastTripCrew } from "../services/consoleApi";
import LocationPicker, { LatLng } from "./LocationPicker";
import "./SchoolTripComposer.css";

export interface ComposerBusChild {
  id: string;
  name: string;
  parentId?: string;
  classLabel?: string;
  temporaryTripChild?: boolean;
}
/* driverLabel: the resolved driver name(s) for the BUS picker; undefined when
   none could be resolved (2026-09-08, Gabriel: the dropdown shows the driver). */
export interface ComposerBus { id: string; name: string; plate: string; driverLabel?: string; children: ComposerBusChild[]; }

interface Props {
  fleetOrgCode: string;
  buses: ComposerBus[];
  canHire: boolean;
  onClose: () => void;
  /** The console's dark theme — the planner renders outside its tree. */
  dark?: boolean;
}

type FleetLeg = { lid: string; kind: "fleet"; busId: string; useFleetCrew: boolean; childKeys: Set<string>; condName: string; condUsername: string; condPassword: string; condPhone: string };
type HiredLeg = { lid: string; kind: "hired"; nickname: string; plate: string; childKeys: Set<string>; condName: string; condUsername: string; condPassword: string; condPhone: string };
type Leg = FleetLeg | HiredLeg;

// An "outside child" group: one temporary guardian/parent + the kids they're
// bringing who are NOT in the fleet, assigned to a vehicle (leg) on the trip.
type OutsideChild = { ocid: string; name: string; classLabel: string };
type OutsideGroup = { ogid: string; parentName: string; parentPhone: string; lid: string; children: OutsideChild[] };

interface SuccessState {
  title: string; destination: string; departLabel: string;
  studentCount: number; vehicleCount: number;
  conductors: { vehicle: string; cred: ConductorCreated }[];
  parents: { vehicle: string; cred: TripParentCreated }[];
  fleetDrivers: { vehicle: string; name: string }[];
  warnings: string[];
  /** What /api/trip/notify actually reported — null when the push was never
   *  attempted (backend off) or the call itself threw. The success screen
   *  used to hardcode "Parents got a push" while discarding this. */
  pushSent: number | null;
  pushTotal: number | null;
}

const rid = () => Math.random().toString(36).slice(2, 10);
const childKey = (busId: string, childId: string) => `${busId}::${childId}`;

/* Identity key for a phone number. Kenyan numbers are typed three ways for the
   same handset — 0712…, 254712…, +254 712… — so compare on the last 9 digits
   and they collapse to one. Used to stop two people sharing a number: the trip
   SMS carries a one-time login, so a shared handset receives both people's
   credentials (and the second reveal link overwrites the first). */
const phoneKey = (raw: string) => {
  const digits = (raw || "").replace(/\D/g, "");
  return digits.length > 9 ? digits.slice(-9) : digits;
};
const parseKey = (k: string) => { const i = k.indexOf("::"); return i <= 0 ? null : { busId: k.slice(0, i), childId: k.slice(i + 2) }; };
const initials = (n: string) => (n || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

const BusIcon = () => <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6v6M16 6v6M3 12h18M5 17V7a2 2 0 012-2h10a2 2 0 012 2v10M5 17h14M7 20v-3M17 20v-3" /></svg>;

/* ── "Recents" ─────────────────────────────────────────────────────────────
   2026-09-08, Gabriel: "anytime they are putting data for a hired bus, a
   conductor, outside parents and their children, we have an option there, like
   a small bar written recents, and it has a dropdown column of the ones who
   were keyed before into this organisation and they can select them again."

   Every list below is DISPLAY of data the product already keeps. Nothing new is
   retained anywhere:
     · outside guardians → users/{uid} with temporaryTripParent (never deleted)
     · hired vehicles    → schoolTrips.releasedVehicles[], written by the retire
                           cron when it deletes a finished hire
     · conductors        → /api/trip/past-crew, because firestore.rules denies
                           tripConductors (where the phone lives) to every client

   A PASSWORD IS NEVER PREFILLED OR SHOWN. Credentials are show-once by design
   and every hire mints fresh ones — validate() still demands a new password. */

/** One row of a Recents dropdown: name · contact · email, plus a quiet note. */
type RecentRow = {
  id: string;
  name: string;
  contact: string;
  email?: string;
  /** Right-hand hint — "12 Aug 26", "2 children". */
  meta?: string;
  /** Set = the row is greyed and unpickable, and this says why. */
  disabledNote?: string;
};

type RecentParent = { uid: string; name: string; phone: string; email: string; children: { name: string; classLabel: string }[]; atMs: number | null };
type RecentVehicle = { plate: string; nickname: string; atMs: number | null };
type RecentCrew = PastTripCrew & { atMs: number | null };

const RECENTS_MAX_ROWS = 40;
const RECENTS_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
/* How many documents a scan may read before the sort-and-cap in memory.
   The two scans are NOT equally honest about the word "recent", and the
   difference is a deployed index:
     · schoolTrips IS sorted server-side — the composite
       (schoolCode ASC, departureAt DESC) has been in firestore.indexes.json
       since 2026-07-25 and was deployed 09-06 — so this cap really does take the
       newest trips. (BusTracking's "no orderBy, needs a composite index" note on
       the Trips tab is from 07-04 and predates that index; it is stale.)
     · users (the outside guardians) is NOT. There is no
       (schoolCode, temporaryTripParent, createdAt) index, so Firestore returns an
       arbitrary page ordered by document id — `tripp_<token>`, i.e. random.
       Under this ceiling the in-memory sort in trimRecents hides that. Above it —
       roughly 20 trips at 10 outside guardians each — a guardian keyed in last
       week can be missing from Recents, silently and permanently. The fix is that
       index in pear_mobile/firestore.indexes.json and an orderBy on the query
       below, NOT a bigger number here (which only raises the bill). */
const RECENTS_SCAN_LIMIT = 200;

/** Backend `normalizePlate` (trips.js), verbatim — a hired bus document's id
 *  IS its normalised plate, which is what makes the "already exists" check
 *  below match the server's. */
const plateKey = (raw: string) => String(raw || "").toUpperCase().replace(/[\s-]/g, "");

/** Millis out of a Firestore Timestamp / Date / number / serialised stamp. */
function tsMillis(v: unknown): number | null {
  if (v instanceof Timestamp) return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (v && typeof v === "object") {
    const sec = (v as { seconds?: number; _seconds?: number }).seconds
      ?? (v as { _seconds?: number })._seconds;
    if (typeof sec === "number" && Number.isFinite(sec)) return sec * 1000;
  }
  return null;
}

const usedLabel = (ms: number | null) =>
  (ms ? new Date(ms).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "");

/** Newest first, de-duplicated on `keyOf`, dropped past a year old, capped.
 *  Same guardian keyed for three trips is three user documents and one person. */
function trimRecents<T extends { atMs: number | null }>(rows: T[], keyOf: (r: T) => string): T[] {
  const cutoff = Date.now() - RECENTS_MAX_AGE_MS;
  const seen = new Set<string>();
  const out: T[] = [];
  const ordered = rows
    .filter((r) => r.atMs === null || r.atMs >= cutoff)
    .sort((a, b) => (b.atMs ?? 0) - (a.atMs ?? 0));
  for (const r of ordered) {
    const k = keyOf(r);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(r);
    if (out.length >= RECENTS_MAX_ROWS) break;
  }
  return out;
}

const crewKey = (c: { username: string; phone: string; displayName: string }) =>
  c.username.trim().toLowerCase() || phoneKey(c.phone) || c.displayName.trim().toLowerCase();

/** One-shot lazy loader for a Recents list.
 *  💸 NOTHING is read until the bar is actually opened. Blaze bills every
 *  document read whether anyone looks or not, and most trips are typed
 *  straight in — so an unopened picker must cost exactly zero. */
function useRecents<T>(load: () => Promise<T[]>) {
  const [rows, setRows] = useState<T[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadRef = useRef(load);
  loadRef.current = load;
  const started = useRef(false);
  const alive = useRef(true);
  // Re-armed on mount, not just cleared on unmount: React 18 StrictMode mounts,
  // unmounts and remounts in dev, and a one-way flag would latch false there and
  // leave every list permanently empty.
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const open = useCallback(() => {
    if (started.current) return;
    started.current = true;
    setBusy(true);
    loadRef.current()
      .then((r) => { if (alive.current) setRows(r); })
      .catch((e) => { if (alive.current) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (alive.current) setBusy(false); });
  }, []);
  return { rows, busy, error, open };
}

/** The bar itself. Closed it is one quiet line; open it is a short list that
 *  scrolls inside itself, in normal flow — no floating panel to be clipped by
 *  the composer's scrolling column. */
function RecentsBar({ note, cols, rows, busy, error, empty, onOpen, onPick }: {
  note: string;
  cols: 2 | 3;
  rows: RecentRow[];
  busy: boolean;
  error: string | null;
  empty: string;
  onOpen: () => void;
  onPick: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const msg = (t: string, color: string) => (
    <div style={{ padding: "10px 12px", fontSize: 12, color }}>{t}</div>
  );
  return (
    <div style={{ marginBottom: 10 }}>
      <button
        type="button"
        onClick={() => { const next = !open; setOpen(next); if (next) onOpen(); }}
        style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "var(--stc-s2)", border: "1px solid var(--stc-line2)", borderRadius: 10, padding: "7px 11px", fontSize: 12, fontWeight: 700, color: "var(--stc-ink)", fontFamily: "inherit", cursor: "pointer" }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
        Recents
        <span style={{ color: "var(--stc-faint)", fontWeight: 500 }}>{note}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "none" }}><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <div style={{ marginTop: 6, border: "1px solid var(--stc-line2)", borderRadius: 12, background: "var(--stc-s1)", maxHeight: 196, overflowY: "auto" }}>
          {busy
            ? msg("Loading…", "var(--stc-faint)")
            : error
              ? msg(error, "var(--stc-warn)")
              : rows.length === 0
                ? msg(empty, "var(--stc-faint)")
                : rows.map((r, i) => (
                  <div
                    key={r.id}
                    onClick={() => { if (r.disabledNote) return; onPick(r.id); setOpen(false); }}
                    title={r.disabledNote || "Use these details"}
                    style={{
                      display: "grid",
                      gridTemplateColumns: cols === 3
                        ? "minmax(0,1.15fr) minmax(0,.85fr) minmax(0,1.2fr) auto"
                        : "minmax(0,1fr) minmax(0,1.4fr) auto",
                      gap: 10,
                      alignItems: "center",
                      padding: "9px 12px",
                      borderTop: i === 0 ? "none" : "1px solid var(--stc-line)",
                      fontSize: 12.5,
                      cursor: r.disabledNote ? "not-allowed" : "pointer",
                      opacity: r.disabledNote ? 0.5 : 1,
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "var(--stc-ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
                    <span style={{ color: "var(--stc-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.contact || "—"}</span>
                    {cols === 3 && <span style={{ color: "var(--stc-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.email || "—"}</span>}
                    <span style={{ fontSize: 11, color: r.disabledNote ? "var(--stc-warn)" : "var(--stc-faint)", whiteSpace: "nowrap" }}>{r.disabledNote || r.meta || ""}</span>
                  </div>
                ))}
        </div>
      )}
    </div>
  );
}

export default function SchoolTripComposer({ fleetOrgCode, buses, canHire, onClose, dark = false }: Props) {
  const [tripLength, setTripLength] = useState<"single" | "multi">("single");
  const [title, setTitle] = useState("");
  const [destination, setDestination] = useState("");
  /** Optional confirmed pin for the destination — powers the arrival geofence. */
  const [destinationPin, setDestinationPin] = useState<LatLng | null>(null);
  /** Multi-day: full datetime-local values. */
  const [departLocal, setDepartLocal] = useState("");
  const [endsLocal, setEndsLocal] = useState("");
  /** Single day: date + start time only; tripEndsAt = end of that local day. */
  const [singleDate, setSingleDate] = useState("");
  const [singleDepartTime, setSingleDepartTime] = useState("");
  const [legs, setLegs] = useState<Leg[]>([]);
  const [liveMap, setLiveMap] = useState(true);
  const [targetLid, setTargetLid] = useState("");
  const [groupBy, setGroupBy] = useState<"year" | "bus">("year");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<SuccessState | null>(null);
  const [outsideGroups, setOutsideGroups] = useState<OutsideGroup[]>([]);
  /** Accepted Families links — these parents already have Trasse accounts, so
   *  they are not Outside children (no temp login). Key = `${familyOrg}::${childId}`. */
  const [familyLinks, setFamilyLinks] = useState<FamilySchoolLink[]>([]);
  const [familyPicks, setFamilyPicks] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await listSchoolLinks(fleetOrgCode);
      if (!alive) return;
      setFamilyLinks((res.links || []).filter((l) => l.status === "accepted"));
    })();
    return () => { alive = false; };
  }, [fleetOrgCode]);

  const familyChildRows = useMemo(() => {
    const rows: { key: string; familyOrgCode: string; familyName: string; childId: string; childName: string; parentUid: string | null }[] = [];
    for (const link of familyLinks) {
      const kids = (link.children && link.children.length)
        ? link.children
        : (link.childIds || []).map((id) => ({ childId: id, childName: "Child" }));
      for (const k of kids) {
        if (!k.childId) continue;
        rows.push({
          key: `${link.familyOrgCode}::${k.childId}`,
          familyOrgCode: link.familyOrgCode,
          familyName: link.familyName || link.familyOrgCode,
          childId: k.childId,
          childName: k.childName || "Child",
          parentUid: link.parentUid || null,
        });
      }
    }
    return rows;
  }, [familyLinks]);

  function toggleFamilyChild(key: string) {
    setFamilyPicks((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkLid, setBulkLid] = useState("");

  function addOutsideGroup() {
    setOutsideGroups((p) => [...p, { ogid: rid(), parentName: "", parentPhone: "", lid: legs[0]?.lid ?? "", children: [{ ocid: rid(), name: "", classLabel: "" }] }]);
  }
  function removeOutsideGroup(ogid: string) { setOutsideGroups((p) => p.filter((g) => g.ogid !== ogid)); }
  function updateOutsideGroup(ogid: string, patch: Partial<OutsideGroup>) { setOutsideGroups((p) => p.map((g) => (g.ogid === ogid ? { ...g, ...patch } : g))); }
  function addOutsideChild(ogid: string) { setOutsideGroups((p) => p.map((g) => (g.ogid === ogid ? { ...g, children: [...g.children, { ocid: rid(), name: "", classLabel: "" }] } : g))); }
  function removeOutsideChild(ogid: string, ocid: string) { setOutsideGroups((p) => p.map((g) => (g.ogid === ogid ? { ...g, children: g.children.filter((c) => c.ocid !== ocid) } : g))); }
  function updateOutsideChild(ogid: string, ocid: string, patch: Partial<OutsideChild>) { setOutsideGroups((p) => p.map((g) => (g.ogid === ogid ? { ...g, children: g.children.map((c) => (c.ocid === ocid ? { ...c, ...patch } : c)) } : g))); }

  // The leg every bulk-added guardian is assigned to (they can be changed per
  // guardian afterwards). Falls back to the first vehicle if none picked / stale.
  const effectiveBulkLid = bulkLid && legs.some((l) => l.lid === bulkLid) ? bulkLid : (legs[0]?.lid ?? "");

  // Paste many guardians at once — one per line:
  //   "Aisha Otieno: Liam Otieno (Year 4), Zoe Otieno (Year 2)"
  // Name and kids split on the first ":" / "|" / tab; kids split on "," / ";";
  // a trailing "(...)" on a kid becomes their class/year. A name with no kids
  // still adds a guardian with one blank child row to fill in.
  function addBulkGuardians() {
    if (!effectiveBulkLid) { window.alert("Add a vehicle first, then bulk-add guardians."); return; }
    const parsed: OutsideGroup[] = [];
    for (const raw of bulkText.split("\n")) {
      const line = raw.trim();
      if (!line) continue;
      const sepIdx = (() => { const m = line.search(/[:|\t]/); return m; })();
      const parentName = (sepIdx >= 0 ? line.slice(0, sepIdx) : line).trim();
      if (!parentName) continue;
      const kidsPart = sepIdx >= 0 ? line.slice(sepIdx + 1).trim() : "";
      const children: OutsideChild[] = kidsPart
        .split(/[,;]/)
        .map((t) => t.trim())
        .filter(Boolean)
        .map((tok) => {
          const cm = tok.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
          return { ocid: rid(), name: (cm ? cm[1] : tok).trim(), classLabel: cm ? cm[2].trim() : "" };
        })
        .filter((c) => c.name);
      parsed.push({ ogid: rid(), parentName, lid: effectiveBulkLid, parentPhone: "", children: children.length ? children : [{ ocid: rid(), name: "", classLabel: "" }] });
    }
    if (parsed.length === 0) { window.alert("Nothing to add — type one guardian per line, e.g.  Aisha Otieno: Liam (Year 4), Zoe"); return; }
    setOutsideGroups((p) => [...p, ...parsed]);
    setBulkText("");
    setBulkOpen(false);
  }

  /* ── Recents: the three lazy lists (see the note above `useRecents`) ─────
     Loaded on first open of a bar, then reused for the rest of the composer's
     life. All three are read-only convenience over data that already exists. */

  const recentParents = useRecents<RecentParent>(async () => {
    const byUid = new Map<string, RecentParent>();
    // The `users` list rule only passes when the query PINS an org field it can
    // prove, and org membership is spelled three ways across the product (F12).
    // 💸 But these documents have exactly ONE writer — createTripParent — and it
    // stamps all three fields on every one, so running all three queries would
    // bill three reads of the same documents. Try schoolCode alone and fall
    // through to the others only if it finds nothing.
    let anyRead = false;
    for (const orgField of ["schoolCode", "organizationCode", "orgCode"]) {
      try {
        // No orderBy, and it costs something — see RECENTS_SCAN_LIMIT above:
        // without a (schoolCode, temporaryTripParent, createdAt) index this page
        // is arbitrary, not newest-first, once the org passes the ceiling.
        const snap = await getDocs(query(
          collection(db, "users"),
          where(orgField, "==", fleetOrgCode),
          where("temporaryTripParent", "==", true),
          limit(RECENTS_SCAN_LIMIT),
        ));
        anyRead = true;
        snap.forEach((d) => {
          if (byUid.has(d.id)) return;
          const data = d.data() as Record<string, unknown>;
          // Same children[] shape Accounts reads in BusTracking: rows carry
          // childName + className (createTripParent writes both spellings).
          const kids: { name: string; classLabel: string }[] = [];
          if (Array.isArray(data.children)) {
            for (const c of data.children) {
              if (!c || typeof c !== "object") continue;
              const row = c as { childName?: string; name?: string; className?: string; classLabel?: string };
              const nm = String(row.childName || row.name || "").trim();
              if (nm) kids.push({ name: nm, classLabel: String(row.className || row.classLabel || "").trim() });
            }
          }
          byUid.set(d.id, {
            uid: d.id,
            name: String(data.displayName || data.name || "").trim() || "Guardian",
            phone: String(data.phone || "").trim(),
            email: String(data.email || data.username || "").trim(),
            children: kids,
            atMs: tsMillis(data.createdAt) ?? tsMillis(data.updatedAt),
          });
        });
      } catch { /* one spelling failing must not lose the others */ }
      if (byUid.size > 0) break;
    }
    // All three refused is NOT "nobody has been added yet" — say so, rather than
    // letting a permissions problem read as an empty history.
    if (!anyRead) throw new Error("Couldn't read past guardians for this organisation.");
    return trimRecents([...byUid.values()], (p) => phoneKey(p.phone) || p.name.toLowerCase());
  });

  const recentVehicles = useRecents<RecentVehicle>(async () => {
    // The retire cron writes every finished hire onto its trip as
    // releasedVehicles[] and then deletes the bus document — this is the only
    // record left of a plate the school has actually used.
    const snap = await getDocs(query(
      collection(db, "schoolTrips"),
      where("schoolCode", "==", fleetOrgCode),
      // Sorted by the server so the cap takes the NEWEST trips rather than an
      // arbitrary page by document id. The composite this needs — schoolTrips
      // (schoolCode ASC, departureAt DESC) — is in firestore.indexes.json and
      // was deployed 2026-09-06. Both trip writers always stamp departureAt
      // (this file and BusTracking), so nothing is filtered out by the sort.
      orderBy("departureAt", "desc"),
      limit(RECENTS_SCAN_LIMIT),
    ));
    const out: RecentVehicle[] = [];
    snap.forEach((d) => {
      const data = d.data() as Record<string, unknown>;
      if (!Array.isArray(data.releasedVehicles)) return;
      for (const r of data.releasedVehicles) {
        if (!r || typeof r !== "object") continue;
        const row = r as { numberPlate?: string; plate?: string; nickname?: string; releasedAt?: unknown; expiresAt?: unknown };
        const plate = String(row.numberPlate || row.plate || "").trim();
        if (!plate) continue;
        out.push({
          plate,
          nickname: String(row.nickname || "").trim(),
          atMs: tsMillis(row.releasedAt) ?? tsMillis(row.expiresAt) ?? tsMillis(data.departureAt),
        });
      }
    });
    return trimRecents(out, (v) => plateKey(v.plate));
  });

  const recentCrew = useRecents<RecentCrew>(async () => {
    // fetchPastTripCrew never throws, and a refused call is swallowed the same
    // way an empty one is: this route may simply not be deployed to Render yet,
    // and an amber error repeated on every conductor block would be worse than
    // a quiet "none yet". The outside-guardian list above DOES report its
    // failure, because there no endpoint is involved — a refusal there is
    // always a rules or network problem, never "not shipped".
    const r = await fetchPastTripCrew(fleetOrgCode);
    // CONDUCTORS ONLY. The endpoint reads tripConductors AND tripParents and
    // tags each row `kind`; without this filter the conductor bar would offer
    // the guardians of outside children (2026-09-08). The guardian bar has its
    // own, richer source — users/{uid}, which also carries children[].
    return trimRecents(
      r.crew.filter((c) => c.kind === "conductor").map((c) => ({ ...c, atMs: c.lastUsedMs })),
      crewKey,
    );
  });

  /* Everyone on one trip needs their OWN phone number — validate() rejects a
     duplicate because the SMS carries a one-time login. Grey the row instead
     of letting the save bounce. */
  const takenPhoneKeys = useMemo(() => {
    const s = new Set<string>();
    legs.forEach((l) => {
      if (l.kind === "fleet" && l.useFleetCrew) return; // no temp credentials issued
      const k = phoneKey(l.condPhone);
      if (k) s.add(k);
    });
    outsideGroups.forEach((g) => { const k = phoneKey(g.parentPhone); if (k) s.add(k); });
    return s;
  }, [legs, outsideGroups]);

  /* A plate whose bus document still EXISTS cannot be hired again — the backend
     throws "add it as a Fleet bus instead of a hired bus". A hired bus document
     is keyed BY its normalised plate, so the fleet list is the check.

     What this catches: a plate the school later added as a real fleet bus.
     What it cannot: a bus doc this dashboard never sees — a hire that is still
     live on another trip, or a deactivated vehicle (the fleet query filters
     `temporaryHire` and `isActive`). Those still fail server-side, and save()
     already puts the backend's own sentence on the success screen verbatim. */
  const fleetPlateKeys = useMemo(() => {
    const s = new Set<string>();
    buses.forEach((b) => { [plateKey(b.id), plateKey(b.plate)].forEach((k) => { if (k) s.add(k); }); });
    return s;
  }, [buses]);

  const takenPlateKeys = useMemo(() => {
    const s = new Set<string>();
    legs.forEach((l) => { if (l.kind !== "hired") return; const k = plateKey(l.plate); if (k) s.add(k); });
    return s;
  }, [legs]);

  const vehicleRecentRows: RecentRow[] = useMemo(() => (recentVehicles.rows || []).map((v) => {
    const k = plateKey(v.plate);
    return {
      id: k,
      name: v.plate,
      contact: v.nickname,
      meta: usedLabel(v.atMs),
      disabledNote: fleetPlateKeys.has(k)
        ? "already a bus in your fleet"
        : takenPlateKeys.has(k) ? "already on this trip" : undefined,
    };
  }), [recentVehicles.rows, fleetPlateKeys, takenPlateKeys]);

  const crewRecentRows: RecentRow[] = useMemo(() => (recentCrew.rows || []).map((c) => {
    const pk = phoneKey(c.phone);
    return {
      id: crewKey(c),
      name: c.displayName,
      contact: c.phone,
      email: c.email,
      meta: usedLabel(c.atMs),
      // The phone is the test, not the login id: this picker never fills a
      // username, so a username already typed into a leg is the admin's own
      // doing and validate() judges it at save (2026-09-08).
      disabledNote: pk && takenPhoneKeys.has(pk) ? "already on this trip" : undefined,
    };
  }), [recentCrew.rows, takenPhoneKeys]);

  const parentRecentRows: RecentRow[] = useMemo(() => (recentParents.rows || []).map((p) => {
    const pk = phoneKey(p.phone);
    return {
      id: p.uid,
      name: p.name,
      contact: p.phone,
      email: p.email,
      meta: p.children.length ? `${p.children.length} child${p.children.length === 1 ? "" : "ren"}` : usedLabel(p.atMs),
      disabledNote: pk && takenPhoneKeys.has(pk) ? "already on this trip" : undefined,
    };
  }), [recentParents.rows, takenPhoneKeys]);

  function applyRecentVehicle(lid: string, rowId: string) {
    const v = (recentVehicles.rows || []).find((x) => plateKey(x.plate) === rowId);
    if (!v) return;
    updateLeg(lid, { plate: v.plate, ...(v.nickname ? { nickname: v.nickname } : {}) });
  }

  function applyRecentCrew(lid: string, rowId: string) {
    const c = (recentCrew.rows || []).find((x) => crewKey(x) === rowId);
    if (!c) return;
    /* NAME AND PHONE ONLY — never the username, never the password (2026-09-08).
       Filling condUsername from here would be destructive, not convenient:
       createTripConductor (backend src/tripConductors.js) is idempotent ON THE
       USERNAME. It keeps the existing uid and merge-writes a NEW passwordHash,
       tripId, busId and access window over both tripConductors/{username} and
       users/{uid}. And listPastTripCrew sorts by updatedAt with no expiry
       filter, so the row offered FIRST is normally the crew of the org's most
       recent trip — very often one still running. One click plus Create would
       then rotate that conductor's password and re-point them at the new trip
       mid-journey, with nobody told. Every hire mints a fresh login id and a
       fresh password; that IS the design, and validate() demands both. */
    updateLeg(lid, { condName: c.displayName, condPhone: c.phone });
  }

  function applyRecentParent(uid: string) {
    const p = (recentParents.rows || []).find((x) => x.uid === uid);
    if (!p) return;
    setOutsideGroups((prev) => [...prev, {
      ogid: rid(),
      parentName: p.name,
      parentPhone: p.phone,
      lid: legs[0]?.lid ?? "",
      children: p.children.length
        ? p.children.map((c) => ({ ocid: rid(), name: c.name, classLabel: c.classLabel }))
        : [{ ocid: rid(), name: "", classLabel: "" }],
    }]);
  }

  const vehicleLabelForLid = (lid: string): string => {
    const l = legs.find((x) => x.lid === lid);
    if (!l) return "vehicle";
    return l.kind === "fleet" ? (buses.find((b) => b.id === (l as FleetLeg).busId)?.name || "Fleet bus") : ((l as HiredLeg).nickname || "Hired bus");
  };

  const childToParent = useMemo(() => { const m = new Map<string, string | undefined>(); buses.forEach((b) => b.children.forEach((c) => m.set(c.id, c.parentId))); return m; }, [buses]);

  const allSelectedKeys = useMemo(() => { const s = new Set<string>(); legs.forEach((l) => l.childKeys.forEach((k) => s.add(k))); return s; }, [legs]);
  const hasHired = legs.some((l) => l.kind === "hired");
  const targetLeg = legs.find((l) => l.lid === targetLid) || null;

  function addFleet() {
    const lid = rid();
    setLegs((p) => [...p, { lid, kind: "fleet", busId: buses[0]?.id ?? "", useFleetCrew: true, childKeys: new Set(), condName: "", condUsername: "", condPassword: "", condPhone: "" }]);
    setTargetLid(lid);
  }
  function addHired() {
    if (!canHire) { window.alert("Hired buses are a full-feature option. Contact the team to enable it."); return; }
    const lid = rid();
    setLegs((p) => [...p, { lid, kind: "hired", nickname: "", plate: "", childKeys: new Set(), condName: "", condUsername: "", condPassword: "", condPhone: "" }]);
    setTargetLid(lid);
  }
  function updateLeg(lid: string, patch: Partial<FleetLeg> & Partial<HiredLeg>) {
    setLegs((p) => p.map((l) => (l.lid === lid ? ({ ...l, ...patch } as Leg) : l)));
  }
  function removeLeg(lid: string) {
    setLegs((p) => p.filter((l) => l.lid !== lid));
    if (targetLid === lid) setTargetLid("");
  }
  function toggleChild(lid: string, key: string) {
    setLegs((p) => p.map((l) => {
      if (l.lid !== lid) return l;
      const next = new Set(l.childKeys);
      if (next.has(key)) next.delete(key); else next.add(key);
      return { ...l, childKeys: next } as Leg;
    }));
  }

  // Students available for the current target leg.
  const pickerRows = useMemo(() => {
    if (!targetLeg) return [] as { key: string; name: string; year: string; bus: string; busId: string }[];
    const fromBuses = targetLeg.kind === "fleet" ? buses.filter((b) => b.id === (targetLeg as FleetLeg).busId) : buses;
    const rows: { key: string; name: string; year: string; bus: string; busId: string }[] = [];
    fromBuses.forEach((b) => b.children.forEach((c) => {
      // Outside-trip kids left on a bus roster after a prior trip must not appear
      // in the permanent fleet picker — add them again via Outside children.
      if ((c as { temporaryTripChild?: boolean }).temporaryTripChild) return;
      if (c.id.startsWith("otrip_")) return;
      if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return;
      rows.push({ key: childKey(b.id, c.id), name: c.name, year: c.classLabel || "No year group", bus: b.name, busId: b.id });
    }));
    return rows;
  }, [targetLeg, buses, search]);

  const grouped = useMemo(() => {
    const m = new Map<string, typeof pickerRows>();
    pickerRows.forEach((r) => { const g = groupBy === "year" ? r.year : r.bus; if (!m.has(g)) m.set(g, []); m.get(g)!.push(r); });
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [pickerRows, groupBy]);

  /** Local calendar end-of-day (23:59:59.999) for a YYYY-MM-DD date string. */
  function endOfLocalDayMs(dateYmd: string): number {
    const [y, m, d] = dateYmd.split("-").map(Number);
    if (!y || !m || !d) return NaN;
    return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
  }

  /** Resolve departure + trip-end millis from the length-specific fields. */
  function resolveTripTimes(): { depMs: number; endsMs: number } | null {
    if (tripLength === "single") {
      if (!singleDate || !singleDepartTime) return null;
      const depMs = new Date(`${singleDate}T${singleDepartTime}`).getTime();
      const endsMs = endOfLocalDayMs(singleDate);
      if (!Number.isFinite(depMs) || !Number.isFinite(endsMs)) return null;
      return { depMs, endsMs };
    }
    if (!departLocal) return null;
    const depMs = new Date(departLocal).getTime();
    if (!Number.isFinite(depMs)) return null;
    const endsMs = endsLocal
      ? new Date(endsLocal).getTime()
      : depMs + 12 * 60 * 60 * 1000;
    if (!Number.isFinite(endsMs)) return null;
    return { depMs, endsMs };
  }

  function validate(): string | null {
    if (!title.trim() || !destination.trim()) return "Enter a trip title and destination.";
    if (tripLength === "single") {
      if (!singleDate || !singleDepartTime) return "Enter the trip date and departure time.";
    } else if (!departLocal) {
      return "Enter a trip title, destination and departure time.";
    }
    if (legs.length === 0) return "Add at least one vehicle (fleet or hired).";
    const times = resolveTripTimes();
    if (!times) return "Invalid departure time.";
    const { depMs, endsMs } = times;
    if (endsMs <= depMs) return "Trip end must be after departure.";
    if (hasHired) {
      if (tripLength === "multi" && !endsLocal) {
        return "Enter when the trip ends (sets hired-bus expiry and conductor login window).";
      }
      if (endsMs <= Date.now()) return "Trip end must be in the future.";
    }
    const used = new Set<string>();
    for (let i = 0; i < legs.length; i++) {
      const l = legs[i];
      // Linked Families children ride the first vehicle (v1 — no per-child bus picker).
      if (l.childKeys.size === 0 && !(familyPicks.size > 0 && i === 0)) {
        return "Each vehicle needs at least one student.";
      }
      for (const k of l.childKeys) { if (used.has(k)) return "A student is on more than one vehicle."; used.add(k); }
      if (l.kind === "fleet") {
        if (!l.busId) return "Choose a fleet bus for each fleet row.";
        if (!l.useFleetCrew && (!l.condUsername.trim() || l.condPassword.length < 6)) return "Conductor needs a username and a 6+ char password.";
        if (!l.useFleetCrew && !l.condPhone.trim()) return "Enter a phone for the conductor so we can SMS the one-time login link.";
        if (!l.useFleetCrew && !/[\d+]{8,}/.test(l.condPhone.replace(/\s/g, ""))) return "Conductor phone number looks invalid.";
      } else {
        if (!l.nickname.trim()) return "Give each hired bus a display name.";
        if (!l.plate.trim()) return "Enter the number plate for each hired bus — parents are told which bus their child is on.";
        if (!l.condUsername.trim() || l.condPassword.length < 6) return "Each hired bus needs a conductor username + 6+ char password.";
        if (!l.condPhone.trim()) return "Enter a phone number for each hired bus so we can SMS the one-time login link.";
        if (!/[\d+]{8,}/.test(l.condPhone.replace(/\s/g, ""))) return "Hired-bus phone number looks invalid.";
      }
    }
    for (const g of outsideGroups) {
      if (!g.parentName.trim()) return "Each outside child needs a guardian/parent name.";
      if (!g.parentPhone.trim()) return "Enter a phone for each outside guardian so we can SMS their trip login.";
      if (!/[\d+]{8,}/.test(g.parentPhone.replace(/\s/g, ""))) return "Outside guardian phone number looks invalid.";
      if (!g.lid || !legs.some((l) => l.lid === g.lid)) return "Assign each outside child to a vehicle on the trip.";
      if (g.children.filter((c) => c.name.trim()).length === 0) return "Each outside guardian needs at least one child (with a name).";
    }

    /* Nobody on a trip may share a phone number or a username with anyone else
       on it. Both are login identity: the phone receives the one-time reveal
       SMS, the username IS the account. Duplicates silently hand one person
       both logins, which reads as "my code doesn't work" long after the trip. */
    const phoneAt = new Map<string, string>();
    const userAt = new Map<string, string>();
    const legLabel = (l: Leg, i: number) =>
      l.kind === "hired" ? (l.nickname.trim() || `hired bus ${i + 1}`) : (l.busId || `fleet bus ${i + 1}`);

    for (let i = 0; i < legs.length; i++) {
      const l = legs[i];
      // Fleet rows keeping the permanent crew issue no temp credentials.
      if (l.kind === "fleet" && l.useFleetCrew) continue;
      const label = legLabel(l, i);
      const pk = phoneKey(l.condPhone);
      if (pk) {
        const prev = phoneAt.get(pk);
        if (prev) return `${label} and ${prev} have the same conductor phone number. Each conductor needs their own — the SMS carries their one-time login.`;
        phoneAt.set(pk, label);
      }
      const uk = l.condUsername.trim().toLowerCase();
      if (uk) {
        const prev = userAt.get(uk);
        if (prev) return `${label} and ${prev} use the same conductor username. Usernames must be unique — the username is the login itself.`;
        userAt.set(uk, label);
      }
    }
    for (let i = 0; i < outsideGroups.length; i++) {
      const g = outsideGroups[i];
      const pk = phoneKey(g.parentPhone);
      if (!pk) continue;
      const label = g.parentName.trim() || `outside guardian ${i + 1}`;
      const prev = phoneAt.get(pk);
      if (prev) return `${label} and ${prev} have the same phone number. Each person needs their own — the SMS carries their one-time login.`;
      phoneAt.set(pk, label);
    }
    return null;
  }

  async function save() {
    const err = validate();
    if (err) { window.alert(err); return; }
    const times = resolveTripTimes();
    if (!times) { window.alert("Invalid departure time."); return; }
    const { depMs, endsMs } = times;
    const accessFromMillis = depMs - 4 * 24 * 60 * 60 * 1000;
    const accessUntilMillis = endsMs + 24 * 60 * 60 * 1000;

    setSaving(true);
    try {
      // 1. Create the trip doc first so conductors bind to a real tripId.
      const tripRef = await addDoc(collection(db, "schoolTrips"), {
        schoolCode: fleetOrgCode, orgCode: fleetOrgCode,
        title: title.trim(), destination: destination.trim(),
        // The confirmed pin (never a raw Places result — see LocationPicker's
        // note on Google's 30-day rule). The driver phone fences this and
        // notifies parents on arrival; absent, the trip simply has no arrival
        // alert and behaves exactly as before.
        ...(destinationPin
          ? { destinationLat: destinationPin.lat, destinationLng: destinationPin.lng }
          : {}),
        departureAt: Timestamp.fromMillis(depMs),
        tripEndsAt: Timestamp.fromMillis(endsMs),
        tripLength, returnLeg: true, liveMapForParents: liveMap,
        status: "planned", reminderDayOfDepartureSent: false, reminderOneHourBeforeSent: false,
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      const tripId = tripRef.id;

      const busIds: string[] = [];
      const segments: { busId: string; childIds: string[]; kind: string }[] = [];
      const allChildIds: string[] = [];
      const legBusId = new Map<string, string>(); // lid → resolved busId (for outside children)
      const conductors: SuccessState["conductors"] = [];
      const fleetDrivers: SuccessState["fleetDrivers"] = [];
      const crewIds: string[] = []; // fleet driver / conductor uids to also push-notify
      const warnings: string[] = [];
      const backendOn = isTripsApiConfigured();
      const msg = (err: unknown) => (err instanceof Error ? err.message : String(err));

      for (const leg of legs) {
        if (leg.kind === "fleet") {
          const childIds = [...leg.childKeys].map((k) => parseKey(k)!.childId);
          busIds.push(leg.busId); segments.push({ busId: leg.busId, childIds, kind: "fleet" }); allChildIds.push(...childIds); legBusId.set(leg.lid, leg.busId);
          const vehicle = buses.find((b) => b.id === leg.busId)?.name || "Fleet bus";
          if (leg.useFleetCrew) {
            /* The old query filtered on busId+role only — the `users` list rule
               allows org members ONLY when the query PINS an org field it can
               prove (`userDocInCallerOrg()`), so Firestore refused the whole
               save the moment "Use bus driver" was on (Gabriel, 2026-08-24:
               "everything seems to be okay yet it's refusing"). Pin the org —
               and union all three org-field spellings, since drivers are
               written with different ones depending on the surface (F12). */
            const driverDocs = new Map<string, { ref: import("firebase/firestore").DocumentReference; data: Record<string, unknown> }>();
            for (const orgField of ["schoolCode", "organizationCode", "orgCode"]) {
              try {
                const snap = await getDocs(query(collection(db, "users"), where(orgField, "==", fleetOrgCode), where("busId", "==", leg.busId), where("role", "==", "bus_driver")));
                snap.forEach((d) => { if (!driverDocs.has(d.id)) driverDocs.set(d.id, { ref: d.ref, data: d.data() as Record<string, unknown> }); });
              } catch { /* one spelling failing must not lose the others */ }
            }
            if (driverDocs.size > 0) {
              const batch = writeBatch(db);
              driverDocs.forEach(({ ref, data: dd }, id) => { batch.update(ref, { activeSchoolTripId: tripId, updatedAt: serverTimestamp() }); fleetDrivers.push({ vehicle, name: (dd.displayName || dd.name || dd.email || "Driver") as string }); crewIds.push(id); });
              await batch.commit();
            } else {
              warnings.push(`No driver account found on ${vehicle} — the trip is saved, but no fleet driver was flagged for it.`);
            }
          }
          /* Conductor login needs the backend (creates a Firebase account
             server-side) — and it must respect the toggle.

             This tested only "was a username typed", not "does this leg WANT a
             conductor". The fields keep their values when you switch a fleet
             leg back to "Use bus driver" (useFleetCrew: true), so anyone who
             filled the conductor form and then changed their mind still got a
             real Firebase account created, with credentials shown on the
             success screen, for a person who is not on the trip (review,
             09-06). This branch is the FLEET leg only — a hired bus creates its
             conductor through createHireBus below, because it has no fleet
             driver to fall back on. */
          if (!leg.useFleetCrew && leg.condUsername.trim()) {
            if (!backendOn) {
              warnings.push(`Conductor login for ${vehicle} needs the trips backend — skipped. The fleet driver still runs the trip.`);
            } else {
              try {
                const r = await addTripConductor({ orgCode: fleetOrgCode, tripId, busId: leg.busId, conductor: { username: leg.condUsername.trim(), password: leg.condPassword, displayName: leg.condName.trim() || "Trip conductor", phone: leg.condPhone.trim(), accessFromMillis, accessUntilMillis } });
                conductors.push({ vehicle, cred: r.conductor });
                const h = r.conductor.handoff;
                if (h?.emailed) warnings.push(`Reveal link emailed to ${h.emailed} fleet driver(s) on ${vehicle}.`);
                (h?.notes || []).forEach((n) => warnings.push(n));
                if (r.conductor.revealUrl) warnings.push(`One-time reveal link for ${vehicle}: ${r.conductor.revealUrl}`);
                if (r.conductor.sms?.ok) {
                  warnings.push(`SMS reveal + login sent to ${r.conductor.sms.recipient || leg.condPhone.trim()} for conductor on ${vehicle}.`);
                } else if (r.conductor.sms) {
                  warnings.push(`SMS for conductor on ${vehicle} not sent (${r.conductor.sms.error || "skipped"}) — use the reveal link / creds on this screen.`);
                }
              } catch (err) {
                warnings.push(`Couldn't create the conductor login for ${vehicle}: ${msg(err)}`);
              }
            }
          }
        } else {
          // A hired bus genuinely needs the backend (server moves students + mints the account).
          const label = leg.nickname.trim() || "Hired bus";
          if (!backendOn) {
            warnings.push(`Hired bus "${label}" needs the trips backend — skipped. Use a fleet bus to test without it.`);
            continue;
          }
          const moves = [...leg.childKeys].map((k) => { const p = parseKey(k)!; return { sourceBusId: p.busId, childId: p.childId }; });
          try {
            const r = await createHireBus({ orgCode: fleetOrgCode, tripId, nickname: leg.nickname.trim(), numberPlate: leg.plate.trim(), expiresAtMillis: endsMs, moves, conductor: { username: leg.condUsername.trim(), password: leg.condPassword, displayName: leg.condName.trim() || "Trip conductor", phone: leg.condPhone.trim(), accessFromMillis, accessUntilMillis } });
            busIds.push(r.busId); segments.push({ busId: r.busId, childIds: moves.map((m) => m.childId), kind: "hired" }); allChildIds.push(...moves.map((m) => m.childId)); legBusId.set(leg.lid, r.busId);
            if (r.conductor) {
              conductors.push({ vehicle: label, cred: r.conductor });
              if (r.conductor.revealUrl) warnings.push(`One-time reveal link for ${label}: ${r.conductor.revealUrl}`);
            }
            if (r.sms?.ok) {
              warnings.push(`SMS reveal link sent to ${r.sms.recipient || leg.condPhone.trim()} for ${label}.`);
            } else if (r.sms) {
              warnings.push(`SMS for ${label} not sent (${r.sms.error || "skipped"}) — use the reveal link / creds on this screen.`);
            }
          } catch (err) {
            warnings.push(`Couldn't create hired bus "${label}": ${msg(err)}`);
          }
        }
      }

      // Outside children: one temp guardian per group. Create their account + kids
      // on the chosen trip bus (backend), then fold the returned childIds/parent uid
      // into the trip so live-tracking + reminders + cancellation reach them too.
      const parents: SuccessState["parents"] = [];
      const tempParentIds: string[] = [];
      for (const g of outsideGroups) {
        const kids = g.children.map((c) => ({ name: c.name.trim(), classLabel: c.classLabel.trim() })).filter((c) => c.name);
        if (kids.length === 0) continue;
        const guardian = g.parentName.trim() || "Outside guardian";
        const vehicle = vehicleLabelForLid(g.lid);
        const busId = legBusId.get(g.lid);
        if (!busId) { warnings.push(`Couldn't add ${guardian}'s children — their assigned vehicle (${vehicle}) wasn't created.`); continue; }
        if (!backendOn) { warnings.push(`Outside children for ${vehicle} need the trips backend — skipped.`); continue; }
        try {
          const r = await createTripParent({ orgCode: fleetOrgCode, tripId, busId, displayName: guardian, phone: g.parentPhone.trim(), accessFromMillis, accessUntilMillis: endsMs + 2 * 60 * 60 * 1000, children: kids });
          parents.push({ vehicle, cred: r.parent });
          tempParentIds.push(r.parent.uid);
          allChildIds.push(...r.parent.children.map((c) => c.childId));
          if (r.parent.revealUrl) warnings.push(`One-time reveal link for ${guardian}: ${r.parent.revealUrl}`);
          if (r.parent.sms?.ok) {
            warnings.push(`SMS reveal + login sent to ${r.parent.sms.recipient || g.parentPhone.trim()} for ${guardian}.`);
          } else if (r.parent.sms) {
            warnings.push(`SMS for ${guardian} not sent (${r.parent.sms.error || "skipped"}) — use the reveal link / creds on this screen.`);
          }
        } catch (err) {
          warnings.push(`Couldn't create the parent login for ${guardian}: ${msg(err)}`);
        }
      }

      const parentIds = [...new Set([...allChildIds.map((id) => childToParent.get(id)).filter(Boolean), ...tempParentIds])] as string[];
      await updateDoc(doc(db, "schoolTrips", tripId), { busId: busIds[0] ?? "", busIds, segments, childIds: allChildIds, parentIds, updatedAt: serverTimestamp() });

      // Linked Families children AFTER the trip doc is written: add-to-trip
      // arrayUnions onto childIds/parentIds. Doing it before would be overwritten
      // by the updateDoc above. These parents already have Trasse accounts.
      const familyParentIds: string[] = [];
      for (const key of familyPicks) {
        const sep = key.indexOf("::");
        if (sep <= 0) continue;
        const familyOrgCode = key.slice(0, sep);
        const childId = key.slice(sep + 2);
        const row = familyChildRows.find((r) => r.key === key);
        try {
          const r = await addLinkedChildToTrip({ tripId, familyOrgCode, childId });
          if (!r.ok) {
            warnings.push(r.message || `Couldn't add ${row?.childName || "a linked child"} to the trip.`);
            continue;
          }
          allChildIds.push(childId);
          if (row?.parentUid) familyParentIds.push(row.parentUid);
        } catch (err) {
          warnings.push(`Couldn't add ${row?.childName || "a linked child"}: ${msg(err)}`);
        }
      }

      /* A family-only trip whose linked-family adds ALL failed has no
         children at all — the validation exempted the first vehicle on the
         promise these adds would land, and they run post-hoc against a
         different backend. Finalizing would push drivers into Trip mode for
         an empty bus; cancel the shell instead and say why (review, 09-06). */
      if (allChildIds.length === 0) {
        try {
          await updateDoc(doc(db, "schoolTrips", tripId), {
            status: "cancelled",
            cancelledAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        } catch { /* the empty shell also dies at end-of-day via the cron */ }
        window.alert(
          "The trip was not created: none of the linked-family children could be added"
          + (warnings.length ? ` (${warnings[warnings.length - 1]})` : "")
          + ". Nothing was scheduled.");
        setSaving(false);
        return;
      }

      // Push is best-effort: the parent app + fleet driver app flip into Trip mode
      // straight from Firestore, so a missing/failing backend never blocks the trip.
      // Notify BOTH parents and the assigned fleet driver(s)/conductor(s).
      const recipientIds = [...new Set([...parentIds, ...crewIds, ...familyParentIds])];
      // Impromptu = the trip departs within 6 h. 2026-09-08: this branch used to
      // say "happening now … your child's app has switched to Trip mode" — both
      // false at creation time (nothing has left, nothing switches) and parents
      // read it as the bus already being gone. Say only what is true: the time,
      // today, and that a departure push follows.
      const impromptu = depMs <= Date.now() + 6 * 60 * 60 * 1000;
      const pushTitle = impromptu ? `Trip today: ${title.trim()}` : `School trip: ${title.trim()}`;
      const pushBody = impromptu
        ? `${title.trim()} departs at ${new Date(depMs).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} today. You'll get a notification the moment the bus leaves.`
        : `${title.trim()} to ${destination.trim()} on ${new Date(depMs).toLocaleString()}. The app switches to Trip mode on the day.`;
      let pushSent: number | null = null;
      let pushTotal: number | null = null;
      if (recipientIds.length && backendOn) {
        try {
          // The server tells us how many devices the push actually reached —
          // sent:0 and sent:12 used to render the identical "Parents got a
          // push" sentence because this result was discarded (Gabriel's Test 2,
          // 09-04: row arrived, no banner, screen claimed success anyway).
          const r = await notifyTripParents({ orgCode: fleetOrgCode, tripId, parentIds: recipientIds, title: pushTitle, body: pushBody });
          pushSent = typeof r?.sent === "number" ? r.sent : null;
          pushTotal = typeof r?.total === "number" ? r.total : recipientIds.length;
          if (pushSent === 0) {
            warnings.push("No phone received the push (stale or missing device tokens — the server log names each one). Parents and drivers still see the trip in the app.");
          }
        } catch (err) {
          warnings.push(`Trip saved, but the push notification didn't send: ${msg(err)}. Parents and drivers still see it in the app.`);
        }
      } else if (recipientIds.length && !backendOn) {
        warnings.push("Push skipped (backend not configured) — parents and drivers still see the trip in the app.");
      }

      setSuccess({ title: title.trim(), destination: destination.trim(), departLabel: new Date(depMs).toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }), studentCount: allChildIds.length, vehicleCount: legs.length, conductors, parents, fleetDrivers, warnings, pushSent, pushTotal });
    } catch (e) {
      window.alert("Could not create the trip: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  if (success) return <SuccessScreen s={success} dark={dark} onClose={onClose} />;

  const depWindow = (() => {
    const times = resolveTripTimes();
    if (!times) return "—";
    if (tripLength === "single") return "ends end of day";
    const mins = Math.round((times.endsMs - times.depMs) / 60000);
    if (mins <= 0) return "—";
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  })();
  // Linked-family parents count too — a family-only trip used to read
  // "notify 0 parents" while in fact pushing to every family parent.
  const parentCount = new Set([
    ...[...allSelectedKeys].map((k) => childToParent.get(parseKey(k)!.childId)).filter(Boolean),
    ...familyChildRows.filter((r) => familyPicks.has(r.key)).map((r) => r.parentUid).filter(Boolean),
  ]).size;

  return (
    <div className={`stc-overlay${dark ? " stc-dark" : ""}`}>
      <div className="stc-topbar">
        <div className="stc-back" onClick={onClose}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg></div>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div className="stc-logo"><BusIcon /></div>
          <div><div className="stc-title">Plan a school trip</div><div className="stc-sub">Fleet console</div></div>
        </div>
        <div className="stc-topbar-actions">
          <div className="stc-draft"><span />Draft autosaved</div>
          <button className="stc-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="stc-btn-primary" disabled={saving} onClick={save}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>{saving ? "Saving…" : "Save & notify parents"}</button>
        </div>
      </div>

      <div className="stc-body">
        <div className="stc-left">
          <div className="stc-steps">
            <span className="stc-step on"><span className="n">1</span>Trip</span><span className="bar" />
            <span className="stc-step on"><span className="n">2</span>Vehicles &amp; crew</span><span className="bar" />
            <span className="stc-step on"><span className="n">3</span>Students</span><span className="bar" />
            <span className="stc-step"><span className="n">4</span>Parent view</span>
          </div>

          {/* 1 · Trip basics */}
          <div className="stc-card">
            <div className="stc-card-head"><div className="stc-card-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg></div><div className="stc-card-title">Trip basics</div></div>
            <div className="stc-label">Trip length</div>
            <div className="stc-seg" style={{ marginBottom: 18 }}>
              <button className={tripLength === "single" ? "on" : ""} onClick={() => setTripLength("single")}>Single day</button>
              <button className={tripLength === "multi" ? "on" : ""} onClick={() => setTripLength("multi")}>Multi-day</button>
            </div>
            {/* Left column stacks title + date/time so it fills the same
                height as the map column — the title used to sit alone beside
                a 220px map with a hole under it, and the dates dangled below
                (Gabriel: "uniform and clean and classy", 09-04). */}
            <div className="stc-grid2">
              <div style={{ display: "grid", gap: 14, alignContent: "start" }}>
                <div><div className="stc-label">Trip title</div><input className="stc-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Zoo Adventure — Year 4" /></div>
                {tripLength === "single" ? (
                  <>
                    <div><div className="stc-label">Date</div><input className="stc-input" type="date" value={singleDate} onChange={(e) => setSingleDate(e.target.value)} /></div>
                    <div><div className="stc-label">Departs (24 hrs)</div><input className="stc-input" type="time" value={singleDepartTime} onChange={(e) => setSingleDepartTime(e.target.value)} /></div>
                  </>
                ) : (
                  <>
                    <div><div className="stc-label">Departs (24 hrs)</div><input className="stc-input" type="datetime-local" value={departLocal} onChange={(e) => setDepartLocal(e.target.value)} /></div>
                    <div><div className="stc-label">Trip ends (24 hrs)</div><input className="stc-input" type="datetime-local" value={endsLocal} onChange={(e) => setEndsLocal(e.target.value)} /></div>
                  </>
                )}
              </div>
              <div>
                {/* ONE LABEL, ONE BAR, THEN THE MAP.
                    ──────────────────────────────────────────────────────────
                    This was a free-text "Destination" input with the picker's
                    own search box stacked underneath it. Gabriel: "it should
                    just be DESTINATION, then under it the bar for searching
                    the location. Not two bars, that's pointless."

                    Two boxes asking the same question can disagree — you could
                    type "Tsavo" and pin a park in Nairobi, and the trip would
                    carry both. Now the search box IS the destination field:
                    typing sets the text, picking a suggestion sets the text and
                    drops the pin, and dragging the pin adjusts it.

                    The `destination` TEXT is still what gets saved and read by
                    the backend and the driver app; the pin stays optional and
                    only powers the arrival alert. A destination with no pin
                    still makes a perfectly good trip, minus the alert. */}
                <div className="stc-label">Destination</div>
                <LocationPicker
                  value={destinationPin}
                  onChange={setDestinationPin}
                  searchValue={destination}
                  onSearchValueChange={setDestination}
                  searchPlaceholder="e.g. Nairobi National Museum"
                  inputClassName="stc-input"
                  label="Pick it from the list or drag the pin — a pin lets the bus tell parents it has arrived"
                  heightPx={220}
                />
              </div>
            </div>
            <div className="stc-hint" style={{ marginTop: 14 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
              {tripLength === "single" ? (
                <div>Ends automatically at <b style={{ color: "var(--stc-ink)" }}>end of day</b> · hired buses &amp; conductor logins auto-expire <b style={{ color: "var(--stc-ink)" }}>1 day after</b> the trip ends.</div>
              ) : (
                <div>Trip window <b style={{ color: "var(--stc-ink)" }}>{depWindow}</b> · hired buses &amp; conductor logins auto-expire <b style={{ color: "var(--stc-ink)" }}>1 day after</b> the trip ends.</div>
              )}
            </div>
          </div>

          {/* 2 · Vehicles & crew */}
          <div className="stc-card">
            <div className="stc-card-head" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}><div className="stc-card-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6v6M16 6v6M3 12h18M5 17V7a2 2 0 012-2h10a2 2 0 012 2v10M5 17h14M7 20v-3M17 20v-3" /></svg></div><div><div className="stc-card-title">Vehicles &amp; crew</div><div className="stc-card-note">{legs.length} total · {legs.filter((l) => l.kind === "fleet").length} fleet · {legs.filter((l) => l.kind === "hired").length} hired</div></div></div>
              <div className="stc-addrow"><button className="stc-add" onClick={addFleet}>+ Fleet bus</button><button className="stc-add dark" onClick={addHired}>+ Hired bus</button></div>
            </div>
            {legs.length === 0 && <div style={{ fontSize: 13, color: "var(--stc-faint)", padding: "8px 2px" }}>No vehicles yet — add a fleet bus or a hired bus.</div>}
            {legs.map((leg, i) => (
              <div key={leg.lid} className={`stc-leg ${leg.kind}`}>
                <div className="stc-leg-head">
                  <div className="stc-leg-tag"><span className="n">{i + 1}</span>{leg.kind === "fleet" ? "Fleet bus" : "Hired bus · temporary"}</div>
                  {leg.kind === "hired" ? <span className="stc-expire">Auto-expires after trip</span> : <span className="stc-card-note">{leg.childKeys.size} students</span>}
                </div>
                {leg.kind === "fleet" ? (
                  <div className="stc-grid2" style={{ alignItems: "end" }}>
                    <div><div className="stc-label">Bus</div>
                      <select className="stc-input" value={leg.busId} onChange={(e) => updateLeg(leg.lid, { busId: e.target.value, childKeys: new Set() })}>
                        {buses.map((b) => (
                          /* "Bus KCE999Z · KCE999Z" — a bus with no nickname is
                             NAMED after its plate, so printing both said the
                             same thing twice (Gabriel, 09-07). Show the plate
                             separately only when the name is not already it.
                             2026-09-08: append the driver in the app's own
                             middle-dot style ("KCE999Z · Daniel Mwangi") so
                             the admin can see WHO they are sending. */
                          <option key={b.id} value={b.id}>
                            {(b.name && b.plate && !b.name.includes(b.plate)
                              ? `${b.name} · ${b.plate}`
                              : (b.name || b.plate))
                              + ` · ${b.driverLabel || "No driver assigned"}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div><div className="stc-label">Crew</div>
                      <div className="stc-crew">
                        <button className={leg.useFleetCrew ? "on" : ""} onClick={() => updateLeg(leg.lid, { useFleetCrew: true })}>Use bus driver</button>
                        <button className={!leg.useFleetCrew ? "on" : ""} onClick={() => updateLeg(leg.lid, { useFleetCrew: false })}>Add conductor</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <RecentsBar
                      note="hired buses"
                      cols={2}
                      rows={vehicleRecentRows}
                      busy={recentVehicles.busy}
                      error={recentVehicles.error}
                      empty="No past hires yet — a hired bus appears here after its trip finishes."
                      onOpen={recentVehicles.open}
                      onPick={(id) => applyRecentVehicle(leg.lid, id)}
                    />
                    <div className="stc-grid2">
                      <div><div className="stc-label">Display name</div><input className="stc-input" value={leg.nickname} onChange={(e) => updateLeg(leg.lid, { nickname: e.target.value })} placeholder="e.g. Zoo Trip Coach" /></div>
                      <div><div className="stc-label">Plate</div><input className="stc-input" value={leg.plate} onChange={(e) => updateLeg(leg.lid, { plate: e.target.value })} placeholder="KCB 220 X" /></div>
                    </div>
                  </>
                )}
                {(leg.kind === "hired" || !(leg as FleetLeg).useFleetCrew) && (
                  <div className="stc-cond">
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 11 }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /></svg><span style={{ fontSize: 13, fontWeight: 700, color: "var(--stc-ink)" }}>Conductor login</span><span style={{ fontSize: 11, color: "var(--stc-faint)" }}>{leg.kind === "hired" ? "— SMS one-time link + login to the phone below" : "— fleet driver gets email + in-app; conductor also gets SMS"}</span></div>
                    <RecentsBar
                      note="conductors · name & phone"
                      cols={3}
                      rows={crewRecentRows}
                      busy={recentCrew.busy}
                      error={recentCrew.error}
                      empty="No past conductors yet — crew you set up on earlier trips appear here."
                      onOpen={recentCrew.open}
                      onPick={(id) => applyRecentCrew(leg.lid, id)}
                    />
                    <div className="stc-cond-grid">
                      <div><div className="stc-small-label">Name</div><input className="stc-input-sm" value={leg.condName} onChange={(e) => updateLeg(leg.lid, { condName: e.target.value })} placeholder="Daniel Mwangi" /></div>
                      <div><div className="stc-small-label">Username</div><input className="stc-input-sm" value={leg.condUsername} onChange={(e) => updateLeg(leg.lid, { condUsername: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, "") })} placeholder="zoocoach.driver" /></div>
                      <div><div className="stc-small-label">Password</div><input className="stc-input-sm" value={leg.condPassword} onChange={(e) => updateLeg(leg.lid, { condPassword: e.target.value })} placeholder="Trip2406!" /></div>
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <div className="stc-small-label">Phone (SMS reveal + login)</div>
                      <input className="stc-input-sm" value={leg.condPhone} onChange={(e) => updateLeg(leg.lid, { condPhone: e.target.value })} placeholder="07XX XXX XXX or +254…" style={{ maxWidth: 280 }} />
                    </div>
                  </div>
                )}
                <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <button className="stc-leg-remove" onClick={() => removeLeg(leg.lid)}>Remove vehicle</button>
                  <span className="stc-card-note">{leg.childKeys.size} students assigned</span>
                </div>
              </div>
            ))}
          </div>

          {/* 3 · Students */}
          <div className="stc-card">
            <div className="stc-card-head"><div className="stc-card-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /></svg></div><div className="stc-card-title">Students</div></div>
            {legs.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--stc-faint)" }}>Add a vehicle first, then assign students to it.</div>
            ) : (
              <>
                <div className="stc-stu-toolbar">
                  <select className="stc-select" value={targetLid} onChange={(e) => setTargetLid(e.target.value)}>
                    <option value="">Assign to…</option>
                    {legs.map((l, i) => <option key={l.lid} value={l.lid}>{i + 1}. {l.kind === "fleet" ? buses.find((b) => b.id === (l as FleetLeg).busId)?.name || "Fleet bus" : (l as HiredLeg).nickname || "Hired bus"}</option>)}
                  </select>
                  <select className="stc-select" value={groupBy} onChange={(e) => setGroupBy(e.target.value as "year" | "bus")}>
                    <option value="year">Group by year group</option>
                    <option value="bus">Group by bus</option>
                  </select>
                  <input className="stc-select" style={{ flex: 1, minWidth: 140 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search students…" />
                </div>
                {!targetLeg ? <div style={{ fontSize: 13, color: "var(--stc-faint)" }}>Choose a vehicle above to start adding students.</div> : grouped.map(([g, rows]) => (
                  <div key={g}>
                    <div className="stc-stu-group">{g}</div>
                    {rows.map((r) => {
                      const selectedHere = targetLeg.childKeys.has(r.key);
                      const takenElsewhere = !selectedHere && allSelectedKeys.has(r.key);
                      return (
                        <div key={r.key} className={`stc-stu ${selectedHere ? "sel" : ""} ${takenElsewhere ? "taken" : ""}`} onClick={() => { if (!takenElsewhere) toggleChild(targetLeg.lid, r.key); }}>
                          <div className="av">{initials(r.name)}</div>
                          <div className="nm">{r.name}<div className="yr">{r.year}{groupBy === "year" ? ` · ${r.bus}` : ""}{takenElsewhere ? " · on another vehicle" : ""}</div></div>
                          <div className="stc-check">{selectedHere && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>}</div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </>
            )}
          </div>
          {/* 3a · Linked Families children (already on Trasse) */}
          <div className="stc-card">
            <div className="stc-card-head">
              <div className="stc-card-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><path d="M9 22V12h6v10" /></svg></div>
              <div>
                <div className="stc-card-title">Trasse Families</div>
                <div className="stc-card-note">Households that already linked to this school. They have a Trasse account — no temporary login. They ride the first vehicle on this trip.</div>
              </div>
            </div>
            {familyChildRows.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--stc-faint)", padding: "8px 2px" }}>
                None yet — a household sends your school code from their Profile, then you accept on Accounts.
              </div>
            ) : (
              familyChildRows.map((r) => {
                const selected = familyPicks.has(r.key);
                return (
                  <div
                    key={r.key}
                    className={`stc-stu ${selected ? "sel" : ""}`}
                    onClick={() => toggleFamilyChild(r.key)}
                  >
                    <div className="av">{initials(r.childName)}</div>
                    <div className="nm">
                      {r.childName}
                      <div className="yr">{r.familyName}</div>
                    </div>
                    <div className="stc-check">{selected && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>}</div>
                  </div>
                );
              })
            )}
          </div>
          {/* 3b · Outside children (not in the fleet) */}
          <div className="stc-card">
            <div className="stc-card-head" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div className="stc-card-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg></div>
                <div><div className="stc-card-title">Outside children</div><div className="stc-card-note">Kids not in the fleet — each guardian gets an auto-generated login + a <b>Trip</b> password (shown once after you save) that expires after the trip</div></div>
              </div>
              <div className="stc-addrow">
                <button className="stc-add" onClick={() => setBulkOpen((v) => !v)} disabled={legs.length === 0} type="button">{bulkOpen ? "Close bulk add" : "Bulk add"}</button>
                <button className="stc-add dark" onClick={addOutsideGroup} disabled={legs.length === 0} type="button">+ Add guardian</button>
              </div>
            </div>
            {legs.length > 0 && (
              <RecentsBar
                note="guardians"
                cols={3}
                rows={parentRecentRows}
                busy={recentParents.busy}
                error={recentParents.error}
                empty="No outside guardians on record yet for this organisation."
                onOpen={recentParents.open}
                onPick={applyRecentParent}
              />
            )}
            {bulkOpen && legs.length > 0 && (
              <div className="stc-cond" style={{ marginTop: 0, marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" /></svg>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--stc-ink)" }}>Bulk add guardians</span>
                  <span style={{ fontSize: 11, color: "var(--stc-faint)" }}>— one per line: <b>Guardian: Child (Year), Child…</b></span>
                </div>
                <textarea
                  className="stc-input"
                  style={{ minHeight: 104, resize: "vertical", lineHeight: 1.55 }}
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  placeholder={"Aisha Otieno: Liam Otieno (Year 4), Zoe Otieno (Year 2)\nDavid Kimani: Grace Kimani\nZahra Miseda: Liam Nyachom (Year 2)"}
                />
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, marginTop: 10, alignItems: "end" }}>
                  <div>
                    <div className="stc-small-label">Everyone rides on</div>
                    <select className="stc-input-sm" value={effectiveBulkLid} onChange={(e) => setBulkLid(e.target.value)}>
                      {legs.map((l, li) => <option key={l.lid} value={l.lid}>{li + 1}. {vehicleLabelForLid(l.lid)}</option>)}
                    </select>
                  </div>
                  <button className="stc-add" style={{ height: 40, padding: "0 18px" }} onClick={addBulkGuardians} type="button">Add all</button>
                </div>
                <div style={{ fontSize: 11.5, color: "var(--stc-faint)", marginTop: 8 }}>Separate kids with commas; put a class in brackets. Each guardian appears below as an editable card — you can change names, kids, or which bus they ride individually.</div>
              </div>
            )}
            {legs.length === 0 && <div style={{ fontSize: 13, color: "var(--stc-faint)", padding: "8px 2px" }}>Add a vehicle first, then you can add outside children to it.</div>}
            {legs.length > 0 && outsideGroups.length === 0 && <div style={{ fontSize: 13, color: "var(--stc-faint)", padding: "8px 2px" }}>None yet — add a guardian to bring children who aren't registered on a bus. They'll get a temporary login to track the trip.</div>}
            {outsideGroups.map((g, i) => (
              <div key={g.ogid} className="stc-leg hired">
                <div className="stc-leg-head">
                  <div className="stc-leg-tag"><span className="n">{i + 1}</span>Guardian · temporary login</div>
                  <span className="stc-expire">Login expires after trip</span>
                </div>
                <div className="stc-grid2">
                  <div><div className="stc-label">Guardian / parent name</div><input className="stc-input" value={g.parentName} onChange={(e) => updateOutsideGroup(g.ogid, { parentName: e.target.value })} placeholder="e.g. Aisha Otieno" /></div>
                  <div><div className="stc-label">Rides on</div>
                    <select className="stc-input" value={g.lid} onChange={(e) => updateOutsideGroup(g.ogid, { lid: e.target.value })}>
                      {legs.map((l, li) => <option key={l.lid} value={l.lid}>{li + 1}. {vehicleLabelForLid(l.lid)}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
                  <div className="stc-label">Phone (SMS reveal + login)</div>
                  <input className="stc-input" value={g.parentPhone} onChange={(e) => updateOutsideGroup(g.ogid, { parentPhone: e.target.value })} placeholder="07XX XXX XXX or +254…" style={{ maxWidth: 320 }} />
                </div>
                <div className="stc-cond">
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 11 }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /></svg><span style={{ fontSize: 13, fontWeight: 700, color: "var(--stc-ink)" }}>Children</span><span style={{ fontSize: 11, color: "var(--stc-faint)" }}>— shown to this guardian when they log in</span></div>
                  {g.children.map((c) => (
                    <div key={c.ocid} className="stc-cond-grid" style={{ marginBottom: 8 }}>
                      <div><div className="stc-small-label">Child name</div><input className="stc-input-sm" value={c.name} onChange={(e) => updateOutsideChild(g.ogid, c.ocid, { name: e.target.value })} placeholder="e.g. Liam Otieno" /></div>
                      <div><div className="stc-small-label">Class / year (optional)</div><input className="stc-input-sm" value={c.classLabel} onChange={(e) => updateOutsideChild(g.ogid, c.ocid, { classLabel: e.target.value })} placeholder="Year 4" /></div>
                      <div style={{ display: "flex", alignItems: "flex-end" }}><button className="stc-leg-remove" onClick={() => removeOutsideChild(g.ogid, c.ocid)} disabled={g.children.length <= 1}>Remove</button></div>
                    </div>
                  ))}
                  <button className="stc-add" style={{ marginTop: 4 }} onClick={() => addOutsideChild(g.ogid)}>+ Add child</button>
                </div>
                <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <button className="stc-leg-remove" onClick={() => removeOutsideGroup(g.ogid)}>Remove guardian</button>
                  <span className="stc-card-note">{g.children.filter((c) => c.name.trim()).length} child(ren)</span>
                </div>
              </div>
            ))}
          </div>

          {/* 4 · Parent view — last step in the flow (no separate side rail) */}
          <div className="stc-card">
            <div className="stc-card-head"><div className="stc-card-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 20l-5 1V5l5-2 6 2 5-2v16l-5 2-6-2zM9 3v17M15 5v17" /></svg></div><div className="stc-card-title">Parent view</div></div>
            <div className="stc-toggle-row">
              <div><div style={{ fontSize: 14, fontWeight: 700, color: "var(--stc-ink)" }}>Live map for parents</div><div style={{ fontSize: 12.5, color: "var(--stc-muted)", marginTop: 2, maxWidth: 460 }}>On: parents follow the bus live. Off: milestone updates only (departed · arrived · returning · headcount).</div></div>
              <button className={`stc-switch ${liveMap ? "on" : ""}`} onClick={() => setLiveMap((v) => !v)}><span className="knob" /></button>
            </div>
            <div style={{ marginTop: 16 }}>
              <div className="stc-rail-h" style={{ marginBottom: 11 }}>Parents will get</div>
              {["A push notification now", "The app switches to Trip mode", "Departure, arrival & return alerts"].map((t) => (
                <div className="stc-will" key={t}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--stc-ok)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>{t}</div>
              ))}
            </div>
            <button className="stc-save-big" style={{ marginTop: 18 }} disabled={saving} onClick={save}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>{saving ? "Saving…" : `Save & notify ${parentCount} parent${parentCount === 1 ? "" : "s"}`}</button>
            <div style={{ textAlign: "center", fontSize: 12, color: "var(--stc-faint)", marginTop: 10 }}>You can edit or cancel the trip any time before it starts.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SuccessScreen({ s, dark, onClose }: { s: SuccessState; dark: boolean; onClose: () => void }) {
  const copy = (t: string) => { try { navigator.clipboard?.writeText(t); } catch { /* ignore */ } };
  return (
    <div className={`stc-success${dark ? " stc-dark" : ""}`}>
      <div className="stc-success-card">
        <div className="stc-success-tick"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--stc-ok)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg></div>
        {/* Say only what the server reported. Apps flip to Trip mode straight
            from Firestore, so that part is true even when the push failed. */}
        <h2>{s.pushSent !== null && s.pushSent > 0 ? "Trip created & parents notified" : "Trip created"}</h2>
        <p><b>{s.title}</b> is live in the app.{" "}
          {s.pushSent === null
            ? "Parents' apps switch to Trip mode on the day."
            : s.pushSent === 0
              ? "The push didn't reach any phone yet — parents still see the trip in the app."
              : s.pushTotal !== null && s.pushSent < s.pushTotal
                ? `${s.pushSent} of ${s.pushTotal} phones got the push — everyone still sees the trip in the app.`
                : "Parents got a push and their app switched to Trip mode."}
        </p>
        <p style={{ marginTop: 4 }}>{s.destination} · {s.departLabel} · {s.studentCount} students · {s.vehicleCount} vehicle{s.vehicleCount === 1 ? "" : "s"}</p>

        <div className="stc-cred-card">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "var(--stc-ink)" }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>Conductor logins to hand over</div>
            <button className="stc-btn-ghost" onClick={() => window.print()}>Print all</button>
          </div>
          {(s.conductors.length > 0 || s.parents.length > 0) && <div className="stc-cred-warn"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--stc-warn)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" /></svg>Passwords are shown here once. Fleet drivers also get email + in-app handoff. Conductors, hired crew, and outside parents get SMS (one-time link + login) when Brevo SMS is configured.</div>}

          {s.conductors.map((c, i) => (
            <div className="stc-cred" key={i}>
              <div className="stc-cred-top"><div className="stc-cred-av">{initials(c.cred.displayName)}</div><div style={{ flex: 1 }}><div style={{ fontWeight: 700, color: "var(--stc-ink)" }}>{c.cred.displayName}</div><div style={{ fontSize: 12, color: "var(--stc-muted)" }}>Conductor · {c.vehicle}</div></div></div>
              <div className="stc-cred-grid">
                <div className="stc-cred-field"><span><div className="stc-small-label">Username</div><span className="val">{c.cred.username}</span></span><button className="stc-copy" onClick={() => copy(c.cred.username)}>Copy</button></div>
                <div className="stc-cred-field pw"><span><div className="stc-small-label">Password</div><span className="val">{c.cred.password}</span></span><button className="stc-copy" onClick={() => copy(c.cred.password)}>Copy</button></div>
              </div>
              {c.cred.revealUrl && (
                <div className="stc-cred-field" style={{ marginTop: 8 }}>
                  <span><div className="stc-small-label">One-time reveal link</div><span className="val" style={{ fontSize: 12, wordBreak: "break-all" }}>{c.cred.revealUrl}</span></span>
                  <button className="stc-copy" onClick={() => copy(c.cred.revealUrl || "")}>Copy</button>
                </div>
              )}
            </div>
          ))}
          {s.parents.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "var(--stc-ink)", marginTop: 18, marginBottom: 6 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--stc-ok)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>Parent logins to hand over (outside children)</div>
          )}
          {s.parents.map((p, i) => (
            <div className="stc-cred" key={`p${i}`}>
              <div className="stc-cred-top"><div className="stc-cred-av" style={{ background: "var(--stc-ok-tint)", color: "var(--stc-ok)" }}>{initials(p.cred.displayName)}</div><div style={{ flex: 1 }}><div style={{ fontWeight: 700, color: "var(--stc-ink)" }}>{p.cred.displayName}</div><div style={{ fontSize: 12, color: "var(--stc-muted)" }}>Parent · {p.cred.children.map((c) => c.name).join(", ") || "—"} · {p.vehicle}</div></div></div>
              <div className="stc-cred-grid">
                <div className="stc-cred-field"><span><div className="stc-small-label">Login</div><span className="val">{p.cred.username}</span></span><button className="stc-copy" onClick={() => copy(p.cred.username)}>Copy</button></div>
                <div className="stc-cred-field pw"><span><div className="stc-small-label">Password</div><span className="val">{p.cred.password}</span></span><button className="stc-copy" onClick={() => copy(p.cred.password)}>Copy</button></div>
              </div>
              {p.cred.revealUrl && (
                <div className="stc-cred-field" style={{ marginTop: 8 }}>
                  <span><div className="stc-small-label">One-time reveal link</div><span className="val" style={{ fontSize: 12, wordBreak: "break-all" }}>{p.cred.revealUrl}</span></span>
                  <button className="stc-copy" onClick={() => copy(p.cred.revealUrl || "")}>Copy</button>
                </div>
              )}
            </div>
          ))}
          {s.fleetDrivers.map((d, i) => (
            <div className="stc-cred" key={`f${i}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div className="stc-cred-top" style={{ marginBottom: 0 }}><div className="stc-cred-av" style={{ background: "var(--stc-warn-tint)", color: "var(--stc-warn)" }}>{initials(d.name)}</div><div><div style={{ fontWeight: 700, color: "var(--stc-ink)" }}>{d.name}</div><div style={{ fontSize: 12, color: "var(--stc-muted)" }}>Fleet driver · {d.vehicle} — uses their normal Pear login</div></div></div>
              <span className="stc-nosetup">✓ No setup</span>
            </div>
          ))}
          {s.conductors.length === 0 && s.parents.length === 0 && s.fleetDrivers.length === 0 && <div style={{ fontSize: 13, color: "var(--stc-muted)", marginTop: 10 }}>No logins to hand over — fleet drivers use their normal Pear login.</div>}
        </div>

        {s.warnings.length > 0 && (
          <div style={{ background: "var(--stc-warn-tint)", border: "0", borderRadius: 12, padding: 14, marginTop: 16 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--stc-warn)", marginBottom: 6 }}>Heads up</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "var(--stc-warn)" }}>
              {s.warnings.map((w, i) => <li key={i} style={{ margin: "3px 0" }}>{w}</li>)}
            </ul>
          </div>
        )}

        <div className="stc-success-actions">
          {/* "View trip dashboard" removed — its onClick was the same onClose
              as Done, two buttons to one place (Gabriel, 09-05). */}
          <button className="stc-done" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
